import { Alert, Box, Typography, alpha, styled } from "@mui/material";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Link } from "react-router-dom";
import type { MigrationState } from "@hooks/useMigrationState";
import { Routes } from "@utils/routes";

export const VALIDATION_ROUTE =
  "/app/registro/firma-electronica-avanzada/validation-challenge";

// Deadlines are set per wave in Chile's time: the day shown is Santiago's,
// whatever the device's time zone. The year is left out because every
// deadline falls within months of today, and a shorter date reads faster.
const deadlineFormat = new Intl.DateTimeFormat("es-CL", {
  day: "numeric",
  month: "long",
  timeZone: "America/Santiago",
});

export const formatDeadline = (isoDate: string) =>
  deadlineFormat.format(new Date(isoDate));

// The longest wait setTimeout accepts; a later deadline is checked again then.
const MAX_TIMER_MS = 2_147_483_647;

/**
 * How long until the state should be asked for again because its deadline
 * passes, or null when nothing changes with time. The server still decides
 * whether the deadline passed: a device clock running ahead only skips the
 * check, it never asks again in a loop.
 */
export const msUntilDeadlineCheck = (
  state: MigrationState | null | undefined,
  now: number = Date.now(),
): number | null => {
  if (!state || state.validated || state.signingBlocked) return null;
  if (!state.validationDeadline) return null;
  const wait = new Date(state.validationDeadline).getTime() - now;

  if (!(wait > 0)) return null;

  // A second late, so the server's clock is already past the deadline.
  return Math.min(wait + 1000, MAX_TIMER_MS);
};

type Tone = "info" | "warning" | "error";

/**
 * One line of a banner. A line that carries the deadline shows `date` in bold
 * between `text` and `after`, so the doctor finds the date at a glance.
 */
export interface BannerLine {
  text: string;
  date?: string;
  after?: string;
}

export interface SignatureMigrationBanner {
  tone: Tone;
  title: BannerLine;
  /** A second line, in the body size; most banners fit in one. */
  text?: BannerLine;
  to: string;
}

/** The line as plain text, bold date included. */
export const bannerLineText = ({ text, date = "", after = "" }: BannerLine) =>
  `${text}${date}${after}`;

/**
 * The one banner a migrating doctor sees, or null. Never names the signing
 * providers, and never says "renovar": their certificate is not expiring.
 */
export const signatureMigrationBanner = (
  state: MigrationState | null | undefined,
): SignatureMigrationBanner | null => {
  if (!state) return null;
  const deadline = state.validationDeadline;

  if (!state.validated) {
    // The server's clock decides when the deadline passed, not the device's.
    if (state.signingBlocked && deadline) {
      return {
        tone: "error",
        title: { text: "Valida tu identidad para volver a emitir documentos" },
        text: {
          text: "El plazo venció el ",
          date: formatDeadline(deadline),
          after: ".",
        },
        to: VALIDATION_ROUTE,
      };
    }

    return {
      tone: "info",
      title: deadline
        ? { text: "Valida tu identidad antes del ", date: formatDeadline(deadline) }
        : { text: "Valida tu identidad" },
      to: VALIDATION_ROUTE,
    };
  }

  // Only a refused PIN is solved with a new one: a certificate BPO kept would
  // discard it too, and waits for support instead.
  if (state.secondaryCertificateError === "invalidPin") {
    return {
      tone: "warning",
      title: { text: "Elige un nuevo PIN para tu firma electrónica" },
      text: {
        text: "No pudimos terminar de actualizar tu firma con tu PIN actual.",
      },
      to: Routes.NUEVO_PIN_FIRMA,
    };
  }

  return null;
};

const StyledArrowForwardIosRoundedIcon = styled(ArrowForwardIosRoundedIcon)(
  ({ theme }) => ({
    color: theme.palette.icon.primary,
  }),
);

// A link, so the keyboard can reach and open it like any other.
const BannerLink = styled(Link)(({ theme }) => ({
  display: "block",
  position: "sticky",
  alignSelf: "center",
  width: "100%",
  margin: "16px 0px",
  color: "inherit",
  textDecoration: "none",
  borderRadius: theme.shape.borderRadius,
  "&:focus-visible": {
    outline: "2px solid #006bd6",
    outlineOffset: "2px",
  },
}));

const StyledAlert = styled(Alert, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: Tone }>(({ theme, tone }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: theme.palette[tone].light,
  "&:hover": {
    backgroundColor: theme.palette[tone].hover ?? alpha(theme.palette[tone].main, 0.18),
    cursor: "pointer",
  },
  "& .MuiAlert-message": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: "4px",
  },
  "& .MuiAlert-icon": {
    color: theme.palette[tone].main,
  },
}));

const InfoContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: Tone }>(({ theme, tone }) => ({
  color: theme.palette[tone].dark,
}));

// The body size, like the title: small type was hard to read on a phone.
const InfoText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: Tone }>(({ theme, tone }) => ({
  color: theme.palette[tone].dark,
}));

const Line: React.FC<{ line: BannerLine }> = ({ line }) => (
  <>
    {line.text}
    {line.date && <strong>{line.date}</strong>}
    {line.after}
  </>
);

/** Shaped like `PendingFeaAuthAlert`, for doctors moving to a new certificate. */
export const SignatureMigrationAlert: React.FC<{
  banner: SignatureMigrationBanner;
}> = ({ banner }) => {
  return (
    <BannerLink to={banner.to}>
      <StyledAlert
        severity={banner.tone}
        tone={banner.tone}
        action={<StyledArrowForwardIosRoundedIcon />}
      >
        <InfoContainer>
          <Title tone={banner.tone}>
            <Line line={banner.title} />
          </Title>
          {banner.text && (
            <InfoText tone={banner.tone}>
              <Line line={banner.text} />
            </InfoText>
          )}
        </InfoContainer>
      </StyledAlert>
    </BannerLink>
  );
};
